import { Component, DestroyRef, ElementRef, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
// Constants
import {
  Answers,
  DateFormats,
  Events,
  FirearmTypes,
  images,
  ModalID,
  Questions,
  State,
  Statements,
  StatusModalIndicator,
} from '../../shared/models/constants';
import {
  absolutifyFflEzUrls,
  buildEventRecord,
  checkIsFirearmInList,
  createDateTimeStamp,
  createDistinctFirearmCaliberList,
  createDistinctManufactureList,
  createDistinctManufactureModelList,
  findFirearm,
  parseFflEzResult,
} from '../../shared/functions';
import {
  FFL,
  BoundBookEntry,
  Firearm,
  EventEntry,
  Note,
  UpdatedBoundBookResult,
} from '../../shared/models/general';
// Services
import { StepProgressBarService } from '../../shared/components/step-progress-bar/step-progress-bar.service';
import { SharedService } from '../../shared/services/shared.service';
// Third Party
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { finalize, Subscription } from 'rxjs';
import { addYears, format, parse } from 'date-fns';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxMaskDirective } from 'ngx-mask';
import { MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { DatePipe } from '@angular/common';
import { AtfFfl } from '../../shared/models/atf-ffl';
import { MatStepper } from '@angular/material/stepper';
import { NetworkSyncService } from 'src/app/shared/services/network-sync.service';

@Component({
  selector: 'app-acquisition',
  templateUrl: './acquisition.component.html',
  styleUrls: ['./acquisition.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, BsDatepickerModule, NgxMaskDirective, MatStepperModule, MatIconModule, TooltipModule, DatePipe],
})
export class AcquisitionComponent {
  @ViewChild('received_from_name') received_from_name_element: ElementRef<any>;
  @ViewChild('date_acquired') date_acquired_element: ElementRef<any>;
  @ViewChild('serial_number') serial_number_element: ElementRef<any>;
  @ViewChild('stepper') stepper: MatStepper;
  private destroyRef = inject(DestroyRef);
  subscriptions = new Subscription();
  acquisition_form: FormGroup;
  new_bb_entry: BoundBookEntry;
  firearm_types = FirearmTypes;
  steps: string[] = [
    'Step 1: Firearm Details',
    'Step 2: Received From',
    'Step 3: Review Acquisition',
    'Step 4: Submit Record',
  ];
  selected_city_options: string[] = [];
  serial_editing_blackout: string = 'white';
  rifle: string = images.rifle;
  today: Date = new Date();

  disableNextBtn: boolean = true;
  nextValidationFields: string[] = [];
  validationAttemptedStep: number | null = null;
  bsConfig: Partial<BsDatepickerConfig>;

  showManufactureSuggestions = false;
  filteredManufacturers: string[] = [];
  distinct_model_list: string[] = [];
  distinct_firearm_calibers: string[] = [];
  distinct_cities: string[] = [];

  constructor(
    protected element: ElementRef,
    private router: Router,
    private fb: FormBuilder,
    public stepProgressBarService: StepProgressBarService,
    public sharedService: SharedService,
    public networkSyncService: NetworkSyncService,
  ) {
    this.acquisition_form = this.fb.group({
      // Step 1
      firearm_upc: new FormControl({ value: null, disabled: false }),
      firearm_type: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      serial_number: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      serial_number2: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      manufacture: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      importer: new FormControl({ value: null, disabled: false }),
      model: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      additional_markings: new FormControl({ value: null, disabled: false }),
      caliber_gauge: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      // Step 2
      ffl: new FormControl(
        { value: null, disabled: false },
        [Validators.required],
        //  [validateFflRegex.bind(this.sharedService)]
      ),
      received_from_name: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      business_name: new FormControl({ value: null, disabled: false }),
      address: new FormControl({ value: null, disabled: false }),
      address2: new FormControl({ value: null, disabled: false }),
      zip_code: new FormControl({ value: null, disabled: false }),
      state: new FormControl({ value: null, disabled: false }),
      city: new FormControl({ value: null, disabled: false }),
      date_acquired: new FormControl({ value: this.today, disabled: false }, [
        Validators.required,
      ]),
      // Step 3
      notes: new FormControl({ value: null, disabled: false }),
    });
    this.subscriptions.add(
      this.acquisition_form.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (
            this.nextValidationFields.length &&
            this.validationAttemptedStep === this.stepper?.selectedIndex
          ) {
            this.nextValidationFields = this.getNextValidationFields();
          }
        }),
    );
    this.bsConfig = Object.assign(
      {},
      {
        containerClass: 'theme-dark-blue',
        dateInputFormat: 'MM/DD/YYYY',
        showWeekNumbers: false,
        isAnimated: true,
        adaptivePosition: true,
      },
    );
    // yes_no_submit
    this.subscriptions.add(
      this.sharedService.yes_no_submit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          // response for correct FFL question
          if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.correct_ffl
          ) {
            const info = response['optional_details'] as FFL;
            this.checkAndUpdateExpiredFFL(info);
          } else if (
            response['answer'] === Answers.no &&
            response['question'] === Questions.correct_ffl
          ) {
            if (this.stepper.selectedIndex === 1) {
              setTimeout(() => {
                this.received_from_name_element.nativeElement.focus();
              }, 250);
            }
          }
          // response use this firearm
          if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.use_this_firearm
          ) {
            const info = response['optional_details'] as Firearm;
            this.patchFirearmInfo(info);
          }
          // response add this firearm
          if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.add_this_firearm
          ) {
            const info = response['optional_details'] as Firearm;
            this.addFirearmToAvailList(info); //may want to open a status model
            this.stepper.next();
            this.enableNextBtn();
          } else if (
            response['answer'] === Answers.no &&
            response['question'] === Questions.add_this_firearm
          ) {
            this.stepper.next();
            this.enableNextBtn();
          }
        }),
    );
    // message_viewed
    this.subscriptions.add(
      this.sharedService.message_viewed$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.sharedService.closeModal(ModalID.message);
        }),
    );
    // bound_book_updated
    this.subscriptions.add(
      this.sharedService.bound_book_updated$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.sharedService.raiseSuccessToast(
            Statements.acquisition_successfully_created,
          );
          // const event_record = this.buildEventRecord(bound_book_entry);
          // await this.sharedService.addEventRecord(event_record);
          this.stepper.selectedIndex = 3;
        }),
    );
    // ffl_selected
    this.subscriptions.add(
      this.sharedService.ffl_selected$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.patchFfl(response);
        }),
    );
    // firearm_list_retrieved$ // remove this subject once app goes to live
    this.subscriptions.add(
      this.sharedService.firearm_list_retrieved$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.getManufactureList();
        }),
    );
  }
  async ngOnInit() {
    this.subscriptions.add(
      this.acquisition_form
        .get('ffl')
        .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          const lengthNoDashes = value ? value.replace(/-/g, '').length : 0;
          if (lengthNoDashes !== 15) {
            this.acquisition_form.patchValue(
              {
                received_from_name: null,
                business_name: null,
                address: null,
                address2: null,
                city: null,
                state: null,
                zip_code: null,
              },
              { emitEvent: false },
            );
          }
        }),
    );
  }
  ngAfterViewInit() {
    this.stepper.selectedIndex = 0; // or your desired initial index
  }
  // Form functions
  findThisFirearm() {
    findFirearm(
      this.sharedService,
      this.acquisition_form.get('firearm_upc').value,
    );
  }
  findStateAndCity() {
    if (this.acquisition_form.get('zip_code').value.length === 5) {
      this.sharedService
        .getCityState(this.acquisition_form.get('zip_code').value)
        .then((response) => {
          if (response) {
            this.distinct_cities = response.cities;
            const city =
              this.distinct_cities.length === 1 ? this.distinct_cities[0] : null;
            this.patchStateAndCity(city, response.state);
          }
        });
    } else {
      this.distinct_cities = [];
      this.acquisition_form.patchValue({
        city: null,
        state: null,
      });
    }
  }
  patchStateAndCity(city: string, state: string) {
    this.acquisition_form.patchValue({
      city: city,
      state: state,
    });
  }
  patchFirearmInfo(response_details: Firearm) {
    if (!this.acquisition_form.get('firearm_upc').value) {
      this.acquisition_form.patchValue({
        firearm_upc: response_details.firearm_upc,
      });
    }
    this.acquisition_form.patchValue({
      firearm_type: response_details.firearm_type,
      manufacture: response_details.manufacture,
      importer: response_details.importer,
      model: response_details.model,
      additional_markings: response_details.additional_markings,
      caliber_gauge: response_details.caliber_gauge,
    });

    const firearmTypeControl = this.acquisition_form.get('firearm_type');
    firearmTypeControl.markAsDirty();
    firearmTypeControl.markAsTouched();
    firearmTypeControl.updateValueAndValidity();

    const manufactureControl = this.acquisition_form.get('manufacture');
    manufactureControl.markAsDirty();
    manufactureControl.markAsTouched();
    manufactureControl.updateValueAndValidity();

    const modelControl = this.acquisition_form.get('model');
    modelControl.markAsDirty();
    modelControl.markAsTouched();
    modelControl.updateValueAndValidity();

    const caliberGaugeControl = this.acquisition_form.get('caliber_gauge');
    caliberGaugeControl.markAsDirty();
    caliberGaugeControl.markAsTouched();
    caliberGaugeControl.updateValueAndValidity();

    if (this.acquisition_form.get('additional_markings').value) {
      const additionalMarkingsControl = this.acquisition_form.get(
        'additional_markings',
      );
      additionalMarkingsControl.markAsDirty();
      additionalMarkingsControl.markAsTouched();
      additionalMarkingsControl.updateValueAndValidity();
      this.acquisition_form.get('additional_markings').updateValueAndValidity();
    }
    if (this.acquisition_form.get('importer').value) {
      const importerControl = this.acquisition_form.get('importer');
      importerControl.markAsDirty();
      importerControl.markAsTouched();
      importerControl.updateValueAndValidity();
      this.acquisition_form.get('importer').updateValueAndValidity();
    }
    if (this.stepper.selectedIndex === 0) {
      setTimeout(() => {
        this.serial_number_element.nativeElement.focus();
      }, 500);
    }
  }
  patchFfl(response_details: FFL) {
    const currentFfl = this.acquisition_form.get('ffl').value || '';
    const currentFflLengthNoDashes = currentFfl.replace(/-/g, '').length;

    // Only patch if the current FFL field doesn't already have a full 15 characters
    if (response_details.ffl && currentFflLengthNoDashes !== 15) {
      this.acquisition_form.patchValue({
        ffl: response_details.ffl,
      });
    }

    this.acquisition_form.patchValue({
      received_from_name: response_details.legal_name,
      business_name: response_details.business_name,
      address: response_details.premise_street,
      city: response_details.premise_city,
      state: response_details.premise_state,
      zip_code: response_details.premise_zip_code,
    });
    const fflControl = this.acquisition_form.get('ffl');
    fflControl.markAsDirty();
    fflControl.markAsTouched();
    fflControl.updateValueAndValidity();

    const receivedFromNameControl =
      this.acquisition_form.get('received_from_name');
    receivedFromNameControl.markAsDirty();
    receivedFromNameControl.markAsTouched();
    receivedFromNameControl.updateValueAndValidity();

    const businessNameControl = this.acquisition_form.get('business_name');
    businessNameControl.markAsDirty();
    businessNameControl.markAsTouched();
    businessNameControl.updateValueAndValidity();

    const dateAcquiredControl = this.acquisition_form.get('date_acquired');
    dateAcquiredControl.markAsDirty();
    dateAcquiredControl.markAsTouched();
    dateAcquiredControl.updateValueAndValidity();

    if (this.stepper.selectedIndex === 1) {
      setTimeout(() => {
        this.date_acquired_element.nativeElement.focus();
      }, 500);
    }
    this.enableNextBtn();
  }

  /**
   * Checks if the entered FFL is a renewal of an expired management record.
   * If found, updates the expired record with the new FFL details.
   * @param newFfl - The newly entered FFL details from the ATF lookup
   */
  private checkAndUpdateExpiredFFL(newFfl: FFL): void {
    if (!newFfl || !newFfl.legal_name) {
      this.patchFfl(newFfl);
      this.distinct_cities = [];

      return;
    }

    // Find expired FFLs with the same legal name
    const expiredRecord = this.sharedService.ffl_list?.find((managedFfl) => {
      // Match by legal name
      if (managedFfl.legal_name !== newFfl.legal_name) {
        return false;
      }

      // Check if the existing record has expired
      if (!managedFfl.expiration_date) {
        return false;
      }

      const expirationDate = new Date(managedFfl.expiration_date);
      const todayDate = new Date();
      // Remove time portion for accurate date comparison
      todayDate.setHours(0, 0, 0, 0);

      return expirationDate < todayDate;
    });

    // If an expired record is found, update only the ffl number with the value from the form
    if (expiredRecord) {
      let newExpirationDate = newFfl.expiration_date || null;
      if (!newExpirationDate && expiredRecord.expiration_date) {
        // ATF did not return an expiration date — advance the old one by 3 years as a fallback
        const oldDate = parse(
          expiredRecord.expiration_date,
          'MM/dd/yyyy',
          new Date(),
        );
        newExpirationDate = format(addYears(oldDate, 3), 'MM/dd/yyyy');
      }
      const updatedRecord: FFL = {
        ...expiredRecord,
        ffl: (this.acquisition_form.get('ffl').value as string)?.toUpperCase(),
        expiration_date: newExpirationDate ?? expiredRecord.expiration_date,
      };
      this.sharedService.editFFL(updatedRecord);
    }

    // Now patch the form with the new FFL details
    this.patchFfl(newFfl);
    this.distinct_cities = [];
  }

  fetchFfl() {
    if (this.networkSyncService.isOnline) {
      if (this.acquisition_form.get('ffl').value.length === 20) {
        const ffl = this.acquisition_form.get('ffl').value.replace(/-/g, '');
        const licsRegn = ffl.charAt(0);
        const licsDis = ffl.substring(1, 3);
        const licsSeq = ffl.slice(-5);
        this.sharedService.openStatusSpinner('Fetching FFL details');
        this.sharedService
          .getFflEzCheck(licsRegn, licsDis, licsSeq)
          .pipe(
            finalize(() => {
              this.sharedService.closeStatusSpinner();
            }),
          )
          .subscribe({
            next: (response) => {
              setTimeout(() => {
                const fixedHtml = absolutifyFflEzUrls(response);
                const parsedFfl = parseFflEzResult(fixedHtml);
                if (parsedFfl.ffl_number.length !== 0) {
                  const fetched_ffl: FFL = new FFL();
                  fetched_ffl.ffl = parsedFfl.ffl_number.toString();
                  fetched_ffl.legal_name = parsedFfl?.license_name.toString();
                  fetched_ffl.business_name =
                    parsedFfl.business_name.toString();
                  fetched_ffl.premise_street =
                    parsedFfl.premise_street.toString();
                  fetched_ffl.premise_city = parsedFfl.premise_city.toString();
                  fetched_ffl.premise_state =
                    parsedFfl.premise_state.toString();
                  fetched_ffl.premise_zip_code =
                    parsedFfl.premise_zip_code.toString();
                  fetched_ffl.phone_number = null;
                  fetched_ffl.email = null;
                  fetched_ffl.notes = null;
                  fetched_ffl.issue_date = parsedFfl.loa_issue_date.toString()
                    ? parsedFfl.loa_issue_date.toString()
                    : null;
                  fetched_ffl.expiration_date =
                    parsedFfl.expiration_date?.toString()
                      ? parsedFfl.expiration_date.toString()
                      : parsedFfl.loa_expiration_date?.toString() || null;
                  this.sharedService.openYesNoModal(
                    Questions.correct_ffl,
                    fetched_ffl,
                    true,
                  );
                } else {
                  this.sharedService.openMessageModal(
                    'Please verify your entry as no FFL was found.',
                  );
                }
              }, 500);
            },
            error: () => {
              this.sharedService.openMessageModal(
                'Failed to fetch FFL details. Please try again.',
              );
            },
          });
      }
    }
  }
  serialEdit() {
    if (
      this.acquisition_form.value.serial_number &&
      !this.acquisition_form.value.serial_number2
    ) {
      this.serial_editing_blackout = 'black';
    }
  }
  compareSerialInputs() {
    if (
      this.acquisition_form.value.serial_number &&
      this.acquisition_form.value.serial_number2
    ) {
      if (
        this.acquisition_form.value.serial_number ===
        this.acquisition_form.value.serial_number2
      ) {
        this.serial_editing_blackout = 'white';
      } else {
        this.serial_editing_blackout = 'lightyellow';
      }
    }
  }
  checkForDuplicateBoundBookEntries() {}

  async addFirearmToAvailList(info: Firearm) {
    this.sharedService.openStatusSpinner('Adding Firearm');
    await this.sharedService.addFirearmToAvailabilityList(info);
    this.sharedService.closeStatusSpinner();
    this.sharedService.raiseSuccessToast(Statements.firearm_successfully_added);
  }
  // General Functions
  buildBoundBookEntry() {
    this.new_bb_entry = new BoundBookEntry();
    this.new_bb_entry.firearm_id = this.findNextBoundBookEntryId();
    this.new_bb_entry.state = State.acquisition;
    // acquisition info
    this.new_bb_entry.a_firearm_upc = this.acquisition_form.get('firearm_upc')
      .value
      ? this.acquisition_form.get('firearm_upc').value
      : null;
    this.new_bb_entry.a_firearm_type = this.acquisition_form
      .get('firearm_type')
      .value.toUpperCase();
    this.new_bb_entry.a_serial_number = this.acquisition_form
      .get('serial_number')
      .value.toUpperCase();
    this.new_bb_entry.a_manufacture = this.acquisition_form
      .get('manufacture')
      .value.toUpperCase();
    this.new_bb_entry.a_importer = this.acquisition_form.get('importer').value
      ? this.acquisition_form.get('importer').value.toUpperCase()
      : null;
    this.new_bb_entry.a_model = this.acquisition_form.get('model').value;
    this.new_bb_entry.a_additional_markings = this.acquisition_form.get(
      'additional_markings',
    ).value
      ? this.acquisition_form.get('additional_markings').value.toUpperCase()
      : null;
    this.new_bb_entry.a_ffl = this.acquisition_form.get('ffl').value
      ? this.acquisition_form.get('ffl').value.toUpperCase()
      : null;
    this.new_bb_entry.a_received_from_name =
      this.acquisition_form.get('received_from_name').value;
    this.new_bb_entry.a_address = this.acquisition_form.get('address').value
      ? this.acquisition_form.get('address').value
      : null;
    this.new_bb_entry.a_address2 = this.acquisition_form.get('address2').value
      ? this.acquisition_form.get('address2').value
      : null;
    this.new_bb_entry.a_business_name = this.acquisition_form.get(
      'business_name',
    ).value
      ? this.acquisition_form.get('business_name').value
      : null;
    this.new_bb_entry.a_caliber_gauge =
      this.acquisition_form.get('caliber_gauge').value;
    this.new_bb_entry.a_zip_code = this.acquisition_form.get('zip_code').value
      ? this.acquisition_form.get('zip_code').value
      : null;
    this.new_bb_entry.a_state = this.acquisition_form.get('state').value
      ? this.acquisition_form.get('state').value
      : null;
    this.new_bb_entry.a_city = this.acquisition_form.get('city').value
      ? this.acquisition_form.get('city').value
      : null;
    if (this.acquisition_form.get('notes').value) {
      let note = new Note();
      note.comment = this.acquisition_form.get('notes').value;
      note.dateTime = createDateTimeStamp();
      note.note_type = Events.create_acquisition;
      note.username = this.sharedService.logged_in_user.username;
      this.new_bb_entry.notes.push(note);
    }
    this.new_bb_entry.event_log.push(
      buildEventRecord(this.sharedService, Events.create_acquisition),
    );
    this.new_bb_entry.a_date_acquired = format(
      this.acquisition_form.get('date_acquired').value,
      DateFormats.M_D_Y_H_M_S,
    );
    // disposition info
    this.new_bb_entry.d_disposed_to = null;
    this.new_bb_entry.d_form4473_id = null;
    this.new_bb_entry.d_first_name = null;
    this.new_bb_entry.d_middle_name = null;
    this.new_bb_entry.d_last_name = null;
    this.new_bb_entry.d_sr_name = null;
    this.new_bb_entry.d_address = null;
    this.new_bb_entry.d_address2 = null;
    this.new_bb_entry.d_zip_code = null;
    this.new_bb_entry.d_state = null;
    this.new_bb_entry.d_city = null;
    return this.new_bb_entry;
  }

  buildFirearmEntry() {
    const new_firearm = new Firearm();
    new_firearm.firearm_upc = this.acquisition_form
      .get('firearm_upc')
      .value?.toUpperCase();
    new_firearm.firearm_type = this.acquisition_form
      .get('firearm_type')
      .value?.toUpperCase();
    new_firearm.manufacture = this.acquisition_form
      .get('manufacture')
      .value?.toUpperCase();
    new_firearm.importer = this.acquisition_form
      .get('importer')
      .value?.toUpperCase();
    new_firearm.model = this.acquisition_form.get('model').value?.toUpperCase();
    new_firearm.additional_markings = this.acquisition_form
      .get('additional_markings')
      .value?.toUpperCase();
    new_firearm.caliber_gauge = this.acquisition_form
      .get('caliber_gauge')
      .value?.toUpperCase();
    return new_firearm;
  }
  findNextBoundBookEntryId() {
    // update this function to filer by boundbook id
    if (this.sharedService.bound_book.length === 0) {
      return 10000;
    } else {
      const max_bb_id: number = Math.max(
        ...this.sharedService.bound_book.map((o) => o.firearm_id),
      );
      return max_bb_id + 1;
    }
  }

  // button functions
  getNextValidationFields(): string[] {
    const invalidFields: string[] = [];
    if (this.stepper.selectedIndex === 0) {
      if (!this.acquisition_form.get('firearm_type').valid) invalidFields.push('Firearm Type');
      if (!this.acquisition_form.get('serial_number').valid) invalidFields.push('Serial Number');
      if (!this.acquisition_form.get('serial_number2').valid) invalidFields.push('Confirm Serial Number');
      if (this.acquisition_form.get('serial_number').value !== this.acquisition_form.get('serial_number2').value) {
        invalidFields.push('Serial numbers must match');
      }
      if (!this.acquisition_form.get('manufacture').valid) invalidFields.push('Manufacture');
      if (!this.acquisition_form.get('model').valid) invalidFields.push('Model');
      if (!this.acquisition_form.get('caliber_gauge').valid) invalidFields.push('Caliber / Gauge');
    } else if (this.stepper.selectedIndex === 1) {
      if (!this.acquisition_form.get('ffl').valid) invalidFields.push('FFL');
      if (!this.acquisition_form.get('received_from_name').valid) invalidFields.push('Received From Name');
      if (!this.acquisition_form.get('date_acquired').valid) invalidFields.push('Date Acquired');
    }

    return invalidFields;
  }

  showNextValidation(): void {
    if (!this.disableNextBtn) return;

    this.validationAttemptedStep = this.stepper.selectedIndex;
    const invalidFields = this.getNextValidationFields();
    this.nextValidationFields = invalidFields.length
      ? invalidFields
      : ['Complete the required fields before continuing'];
  }

  nextStepClick() {
    if (
      this.stepper.selectedIndex === 0 &&
      this.acquisition_form.get('firearm_type').valid &&
      this.acquisition_form.get('firearm_upc').value?.length === 12 &&
      !checkIsFirearmInList(
        this.sharedService,
        this.acquisition_form.get('firearm_upc').value,
      )
    ) {
      this.sharedService.openYesNoModal(
        Questions.add_this_firearm,
        this.buildFirearmEntry(),
        true,
      );
    } else {
      this.nextValidationFields = [];
      this.validationAttemptedStep = null;
      this.stepper.next();
      this.enableNextBtn();
    }
  }
  enableNextBtn() {
    const serialsMatch =
      this.acquisition_form.get('serial_number').value ===
      this.acquisition_form.get('serial_number2').value;

    if (
      this.stepper.selectedIndex === 0 &&
      this.acquisition_form.get('firearm_type').valid &&
      this.acquisition_form.get('serial_number').valid &&
      this.acquisition_form.get('serial_number2').valid &&
      serialsMatch &&
      this.acquisition_form.get('manufacture').valid &&
      this.acquisition_form.get('model').valid &&
      this.acquisition_form.get('caliber_gauge').valid
    ) {
      this.disableNextBtn = false;
    } else if (
      (this.stepper.selectedIndex === 0 &&
        !this.acquisition_form.get('firearm_type').valid) ||
      (this.stepper.selectedIndex === 0 &&
        !this.acquisition_form.get('serial_number').valid) ||
      (this.stepper.selectedIndex === 0 &&
        !this.acquisition_form.get('serial_number2').valid) ||
      (this.stepper.selectedIndex === 0 && !serialsMatch) ||
      (this.stepper.selectedIndex === 0 &&
        !this.acquisition_form.get('manufacture').valid) ||
      (this.stepper.selectedIndex === 0 &&
        !this.acquisition_form.get('model').valid) ||
      (this.stepper.selectedIndex === 0 &&
        !this.acquisition_form.get('caliber_gauge').valid)
    ) {
      this.disableNextBtn = true;
    } else if (
      this.stepper.selectedIndex === 1 &&
      this.acquisition_form.get('received_from_name').valid &&
      this.acquisition_form.get('date_acquired').valid
    ) {
      this.disableNextBtn = false;
    } else if (
      (this.stepper.selectedIndex === 1 &&
        !this.acquisition_form.get('received_from_name').valid) ||
      !this.acquisition_form.get('date_acquired').valid
    ) {
      this.disableNextBtn = true;
    }

    if (this.nextValidationFields.length) {
      this.nextValidationFields = this.getNextValidationFields();
    }
  }
  previousStepClick() {
    //this.stepProgressBarService.previousStep();
    this.nextValidationFields = [];
    this.validationAttemptedStep = null;
    this.stepper.previous();
    this.enableNextBtn();
  }

  onStepSelectionChange(): void {
    this.nextValidationFields = [];
    this.validationAttemptedStep = null;
    this.enableNextBtn();
  }
  async submitAcquisitionClick() {
    const serialNumber = String(
      this.acquisition_form.get('serial_number')?.value || '',
    ).trim();
    const normalizedSerialNumber = serialNumber.toUpperCase();
    const duplicateSerialNumber = this.sharedService.bound_book.some(
      (entry) =>
        String(entry.a_serial_number || '').trim().toUpperCase() ===
        normalizedSerialNumber,
    );

    if (duplicateSerialNumber) {
      this.sharedService.raiseErrorToast(
        `Serial number ${serialNumber} already exists in the bound book.`,
      );
      return;
    }

    const bound_book_entry = this.buildBoundBookEntry();
    this.sharedService.addBoundBookEntry(bound_book_entry);
    // used for testing only
    // let updated_bound_book: UpdatedBoundBookResult = new UpdatedBoundBookResult();
    // this.sharedService.bound_book_updated$.next(updated_bound_book);
  }
  resetAcquisitionFormClick() {
    this.acquisition_form.reset();
    this.acquisition_form.patchValue({
      date_acquired: format(new Date(), DateFormats.M_D_Y),
    });
    this.stepProgressBarService.resetActiveStep();
    if (this.stepper) {
      this.stepper.selectedIndex = 0;
    }
    this.new_bb_entry = new BoundBookEntry();
    this.disableNextBtn = true;
    this.nextValidationFields = [];
    this.validationAttemptedStep = null;
  }
  repeatAcquisitionClick() {
    this.acquisition_form.patchValue({
      serial_number: null,
      serial_number2: null,
      notes: null,
    });
    this.stepProgressBarService.resetActiveStep(); // need to clear the form before patching
    if (this.stepper) {
      this.stepper.selectedIndex = 0;
    }
    const previous_bb_entry: BoundBookEntry = JSON.parse(
      JSON.stringify(this.new_bb_entry),
    );
  }
  fflSearchClick() {
    this.sharedService.openFflSearchModal();
  }
  closeClick() {
    this.router.navigate(['home']);
  }
  openHome() {
    this.router.navigate(['home']);
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  selectManufacturer(name: string) {
    this.acquisition_form.get('manufacture').setValue(name);
    this.showManufactureSuggestions = false;
  }
  hideManufactureSuggestionsWithDelay() {
    setTimeout(() => (this.showManufactureSuggestions = false), 200);
  }
  getManufactureModels() {
    const manufacture = this.acquisition_form.get('manufacture').value;
    this.distinct_model_list = createDistinctManufactureModelList(
      manufacture,
      this.sharedService,
    );
  }
  getCaliberList() {
    const firearm_type = this.acquisition_form.get('firearm_type').value;
    const manufacture = this.acquisition_form.get('manufacture').value || '';
    const model = this.acquisition_form.get('model').value || '';
    this.distinct_firearm_calibers = createDistinctFirearmCaliberList(
      firearm_type,
      this.sharedService,
      manufacture,
      model,
    );
  }
  getManufactureList() {
    const firearm_type = this.acquisition_form.get('firearm_type').value || '';
    this.sharedService.distinct_manufacture_list =
      createDistinctManufactureList(firearm_type, this.sharedService);
  }
  clearDependentFirearmFields(fields: string[]) {
    const patchValues: any = {};
    fields.forEach((field) => (patchValues[field] = null));
    this.acquisition_form.patchValue(patchValues);
  }
}
